import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { servicesData } from "./ServicesData";
import { Phone, Mail, Send, X } from "lucide-react";
import { Helmet } from "react-helmet";
import Header from "../../layout/Header";
import Footer from "../../layout/Footer";

// Brand Colors
const C = {
  white: "#FFFFFF",
  black: "#000000",
  burgundy: "#7A1F2B",
};

const ServicesDetail = () => {
  const { serviceId } = useParams();
  const service = servicesData.find((s) => s.id === serviceId);

  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    topic: service?.title || "",
    message: "",
  });

  const [success, setSuccess] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);

  useEffect(() => {
    window.scrollTo(0, 0);
    setFormData((prev) => ({ ...prev, topic: service?.title || "" }));
  }, [service]);

  useEffect(() => {
    const handleEsc = (e) => {
      if (e.key === "Escape") setSelectedItem(null);
    };
    window.addEventListener("keydown", handleEsc);
    return () => window.removeEventListener("keydown", handleEsc);
  }, []);

  if (!service)
    return (
      <div className="py-40 text-center text-2xl font-black uppercase tracking-widest text-black">
        Service Not Found
      </div>
    );

  const handleChange = (e) => {
    const { name, value } = e.target;
    if (name === "phone" && (value.length > 10 || /[^0-9]/.test(value))) return;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    if (!formData.name || !formData.email || formData.phone.length !== 10) {
      alert("Please fill all fields correctly.");
      return;
    }

    const waMessage = `New Enquiry:%0AService: ${formData.topic}%0AName: ${formData.name}%0AEmail: ${formData.email}%0APhone: ${formData.phone}%0AMessage: ${formData.message}`;

    window.open(
      `https://wa.me/917042513698?text=${waMessage}`,
      "_blank",
      "noopener noreferrer"
    );

    setSuccess(true);
    setFormData({
      name: "",
      email: "",
      phone: "",
      topic: service.title,
      message: "",
    });
    setTimeout(() => setSuccess(false), 3000);
  };

  return (
    <>
      <Helmet>
        <title>{service.helmetTitle}</title>
        <meta name="description" content={service.helmetDescription} />
        <meta name="keywords" content={service.helmetKeywords} />
      </Helmet>

      <Header />

      <div className="bg-white font-serif w-full">
        {/* ── TOP BURGUNDY RULE ── */}
        <div style={{ height: 4, backgroundColor: C.burgundy }} />

        {/* ── HERO SECTION (Image 1) ── */}
        <div className="relative h-[350px] sm:h-[450px] flex items-center justify-center bg-black overflow-hidden">
          {/* Background Image with heavy dark overlay for high contrast */}
          <div
            className="absolute inset-0 bg-cover bg-center opacity-40 grayscale"
            style={{ backgroundImage: `url(${service.bannerImage})` }}
          />
          
          <div className="relative z-10 text-center px-4 max-w-4xl">
            <p className="text-[10px] sm:text-xs uppercase tracking-[0.5em] font-sans font-bold mb-4" style={{ color: C.burgundy }}>
              Practice Area
            </p>
            <h1 className="text-4xl sm:text-5xl md:text-6xl text-white font-black leading-tight mb-6 tracking-wide">
              {service.title}
            </h1>
            <div className="flex justify-center items-center gap-3 text-white/70 text-[10px] sm:text-xs uppercase tracking-widest font-sans font-bold">
              <Link to="/" className="hover:text-white transition-colors">Home</Link>
              <span style={{ color: C.burgundy }}>/</span>
              <span className="text-white">{service.title}</span>
            </div>
          </div>
        </div>

        {/* ── MAIN CONTENT ── */}
        <div className="max-w-6xl mx-auto px-5 sm:px-8 py-16 sm:py-24">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16">
            
            {/* LEFT COLUMN: Content */}
            <div className="lg:col-span-7 xl:col-span-8 space-y-14">
              
              {/* Intro */}
              <div>
                <h2 className="text-2xl sm:text-3xl font-black text-black mb-6 uppercase tracking-wider font-sans">
                  Overview
                </h2>
                <div style={{ width: 40, height: 3, backgroundColor: C.burgundy, marginBottom: 24 }} />
                <p className="text-black/80 leading-relaxed text-lg sm:text-xl">
                  {service.fullDesc?.intro}
                </p>
              </div>

              {/* Dynamic Sections */}
              {service.fullDesc?.sections?.map((section, i) => (
                <div key={i} className="space-y-6">
                  <h3 className="text-xl sm:text-2xl font-bold text-black border-b-2 border-black pb-3">
                    {section.title}
                  </h3>

                  {section.content && (
                    <p className="text-black/70 text-base sm:text-lg leading-relaxed">
                      {section.content}
                    </p>
                  )}

                  {section.list && (
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mt-4">
                      {section.list.map((item, idx) => (
                        <div 
                          key={idx} 
                          className="flex items-start gap-3 p-4 border border-black/10 bg-gray-50 hover:border-black transition-colors"
                        >
                          <div className="w-1.5 h-1.5 mt-2 rounded-full shrink-0" style={{ backgroundColor: C.burgundy }} />
                          <span className="text-sm font-medium text-black/80">{item}</span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              ))}

              {/* Case Highlights (Max 2 Images via .slice) */}
              {service.featureDetails && service.featureDetails.length > 0 && (
                <div>
                  <h3 className="text-xl sm:text-2xl font-black text-black mb-6 uppercase tracking-wider font-sans">
                    Case Highlights
                  </h3>
                  
                  {/* Slice limits this to 2 images max (Images 2 & 3) */}
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                    {service.featureDetails.slice(0, 2).map((item, i) => (
                      <div
                        key={i}
                        onClick={() => setSelectedItem(item)}
                        className="group border-[2px] border-black cursor-pointer hover:-translate-y-1 transition-transform duration-300 bg-white"
                      >
                        <div className="relative h-48 sm:h-56 overflow-hidden border-b-[2px] border-black">
                          <img
                            src={item.img}
                            alt={item.name}
                            className="w-full h-full object-cover grayscale group-hover:grayscale-0 transition-all duration-500"
                          />
                          <div className="absolute inset-0 bg-black/20 group-hover:bg-transparent transition-colors" />
                        </div>
                        <div className="p-5">
                          <h4 className="font-bold text-lg text-black mb-2 leading-tight">
                            {item.name}
                          </h4>
                          <p className="text-xs sm:text-sm text-black/60 line-clamp-2">
                            {item.desc}
                          </p>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {/* RIGHT COLUMN: Sticky Form & Contact */}
            <div className="lg:col-span-5 xl:col-span-4">
              <div className="sticky top-24 space-y-8">
                
                {/* Form Box */}
                <div className="border-[2px] border-black p-8 sm:p-10 bg-white shadow-[8px_8px_0px_0px_rgba(0,0,0,1)]">
                  <h3 className="text-xl font-black mb-2 text-black uppercase tracking-[0.2em] font-sans">
                    Consultation
                  </h3>
                  <p className="text-xs font-sans font-bold text-black/50 uppercase tracking-widest mb-8">
                    Strictly Confidential
                  </p>

                  <form className="space-y-5" onSubmit={handleSubmit}>
                    <input
                      type="text"
                      name="name"
                      placeholder="FULL NAME"
                      value={formData.name}
                      onChange={handleChange}
                      className="w-full bg-transparent border-b-[2px] border-black/20 px-0 py-3 text-sm font-sans font-bold text-black outline-none focus:border-[#7A1F2B] transition-colors placeholder:text-black/30"
                      required
                    />
                    <input
                      type="email"
                      name="email"
                      placeholder="EMAIL ADDRESS"
                      value={formData.email}
                      onChange={handleChange}
                      className="w-full bg-transparent border-b-[2px] border-black/20 px-0 py-3 text-sm font-sans font-bold text-black outline-none focus:border-[#7A1F2B] transition-colors placeholder:text-black/30"
                      required
                    />
                    <input
                      type="tel"
                      name="phone"
                      placeholder="PHONE NUMBER"
                      value={formData.phone}
                      onChange={handleChange}
                      className="w-full bg-transparent border-b-[2px] border-black/20 px-0 py-3 text-sm font-sans font-bold text-black outline-none focus:border-[#7A1F2B] transition-colors placeholder:text-black/30"
                      required
                    />
                    <textarea
                      name="message"
                      rows="3"
                      placeholder="BRIEF MESSAGE..."
                      value={formData.message}
                      onChange={handleChange}
                      className="w-full bg-transparent border-b-[2px] border-black/20 px-0 py-3 text-sm font-sans font-bold text-black outline-none focus:border-[#7A1F2B] transition-colors resize-none placeholder:text-black/30"
                    />
                    
                    <button
                      type="submit"
                      className="w-full mt-4 py-4 text-xs font-black uppercase tracking-[0.3em] font-sans transition-all duration-300 flex items-center justify-center gap-3"
                      style={{
                        backgroundColor: C.black,
                        color: C.white,
                        border: `2px solid ${C.black}`,
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.backgroundColor = C.burgundy;
                        e.currentTarget.style.borderColor = C.burgundy;
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.backgroundColor = C.black;
                        e.currentTarget.style.borderColor = C.black;
                      }}
                    >
                      {success ? "Message Sent" : "Send Enquiry"} <Send size={14} />
                    </button>
                  </form>
                </div>

                {/* Contact Info Box */}
                <div className="bg-black p-8 text-white">
                  <div className="flex items-center gap-4 mb-6">
                    <div className="p-2 bg-[#7A1F2B] shrink-0">
                      <Phone size={16} className="text-white" />
                    </div>
                    <div>
                      <p className="text-[10px] uppercase tracking-widest text-white/50 font-bold mb-1 font-sans">Direct Line</p>
                      <a href="tel:+917042513698" className="font-bold text-sm sm:text-base hover:text-[#7A1F2B] transition-colors">
                        +91 70425 13698  <br/> +91 87682 62710
                      </a>
                    </div>
                  </div>
                  
                  <div className="w-full h-px bg-white/20 mb-6" />

                  <div className="flex items-center gap-4">
                    <div className="p-2 bg-[#7A1F2B] shrink-0">
                      <Mail size={16} className="text-white" />
                    </div>
                    <div className="overflow-hidden">
                      <p className="text-[10px] uppercase tracking-widest text-white/50 font-bold mb-1 font-sans">Email Desk</p>
                      <a href="mailto:raiandassociates369@gmail.com" className="font-bold text-sm hover:text-[#7A1F2B] transition-colors truncate block">
                        raiandassociates369@gmail.com
                      </a>
                    </div>
                  </div>
                </div>

              </div>
            </div>

          </div>
        </div>

        {/* ── IMAGE POPUP MODAL ── */}
        {selectedItem && (
          <div className="fixed inset-0 bg-black/90 flex items-center justify-center z-[100] p-4 backdrop-blur-sm">
            <div className="bg-white max-w-2xl w-full border-[2px] border-black shadow-[12px_12px_0px_0px_rgba(122,31,43,1)] relative flex flex-col md:flex-row">
              
              <button
                onClick={() => setSelectedItem(null)}
                className="absolute -top-4 -right-4 bg-[#7A1F2B] text-white p-2 border-[2px] border-black hover:bg-black transition-colors z-10"
              >
                <X size={20} />
              </button>

              <div className="md:w-1/2 h-64 md:h-auto border-b md:border-b-0 md:border-r-[2px] border-black">
                <img 
                  src={selectedItem.img} 
                  alt={selectedItem.name}
                  className="w-full h-full object-cover grayscale"
                />
              </div>
              
              <div className="p-8 md:w-1/2 flex flex-col justify-center">
                <p className="text-[10px] uppercase tracking-[0.3em] font-sans font-bold mb-3 text-[#7A1F2B]">
                  Case Detail
                </p>
                <h2 className="text-2xl font-black text-black mb-4 leading-tight">
                  {selectedItem.name}
                </h2>
                <div style={{ width: 30, height: 2, backgroundColor: C.black, marginBottom: 16 }} />
                <p className="text-sm text-black/70 leading-relaxed font-medium">
                  {selectedItem.desc}
                </p>
              </div>

            </div>
          </div>
        )}

        {/* ── BOTTOM BURGUNDY RULE ── */}
        <div style={{ height: 4, backgroundColor: C.burgundy }} />
      </div>

      <Footer />
    </>
  );
};

export default ServicesDetail;